import { useState } from 'react';

import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';

import { Field, FormGrid, FormWrapper, Title } from '../styled';

interface StepDetailsProps {
  value: {
    customer_name: string;
    account_name: string;
    customer_email: string;
    password: string;
    confirm_password: string;
  };
  onChange: (value: any) => void;
}

export default function StepDetalhes({ value, onChange }: StepDetailsProps) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const [passwordError, setPasswordError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passwordRules = {
    length: (v: string) => v.length >= 8 && v.length <= 25,
    upper: (v: string) => /[A-Z]/.test(v),
    lower: (v: string) => /[a-z]/.test(v),
    number: (v: string) => /\d/.test(v),
    special: (v: string) => /[^A-Za-z0-9]/.test(v),
  };

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function PasswordRule({ valid, text }: { valid: boolean; text: string }) {
    return (
      <li
        style={{
          fontSize: 13,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span
          style={{
            color: valid ? '#16a34a' : '#dc2626',
            fontWeight: 'bold',
          }}
        >
          {valid ? '✔' : '✖'}
        </span>
        <span
          style={{
            color: valid ? '#16a34a' : '#dc2626',
          }}
        >
          {text}
        </span>
      </li>
    );
  }
  const [accountError, setAccountError] = useState('');

  const ACCOUNT_REGEX = /^(?![0-9 ]+$)(?!-| )[a-zA-Z0-9- ]{1,63}(?<!-| )(\.[a-zA-Z0-9- ]{1,63}(?<!-| ))*$/;

  return (
    <FormWrapper>
      <Title>Detalhes</Title>
      <FormGrid>
        <Field style={{ gridColumn: '1 / span 2' }}>
          <label>Nome do cliente</label>
          <input
            type='text'
            placeholder='Informe seu nome completo'
            value={value.customer_name}
            onChange={(e) => set('customer_name', e.target.value)}
          />
        </Field>

        <Field>
          <label>Nome da conta</label>
          <input
            type='text'
            placeholder='Defina um nome para identificar sua conta'
            value={value.account_name}
            onChange={(e) => {
              const nextAccount = e.target.value;
              set('account_name', nextAccount);

              if (!ACCOUNT_REGEX.test(nextAccount.trim())) {
                setAccountError(
                  'Informe um nome válido que não contenha caracteres especiais',
                );
              } else {
                setAccountError('');
              }
            }}
          />

          {accountError && (
            <span style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>
              {accountError}
            </span>
          )}
        </Field>

        <Field>
          <label>Email do cliente</label>
          <input
            type='email'
            placeholder='ex: meuemail@dominio.com'
            value={value.customer_email}
            onChange={(e) => {
              const nextEmail = e.target.value;
              set('customer_email', nextEmail);

              if (!EMAIL_REGEX.test(nextEmail.trim())) {
                setEmailError(
                  'Informe um e-mail válido (ex: nome@dominio.com)',
                );
              } else {
                setEmailError('');
              }
            }}
          />

          {emailError && (
            <span style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>
              {emailError}
            </span>
          )}
        </Field>

        <Field>
          <label>Senha</label>

          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder='Crie uma senha segura'
              value={value.password}
              onChange={(e) => {
                const pass = e.target.value;
                set('password', pass);

                if (
                  !passwordRules.length(pass) ||
                  !passwordRules.upper(pass) ||
                  !passwordRules.lower(pass) ||
                  !passwordRules.number(pass) ||
                  !passwordRules.special(pass)
                ) {
                  setPasswordError('A senha não atende aos requisitos.');
                  return;
                }

                setPasswordError('');
              }}
              style={{ paddingRight: 40 }}
            />

            <span
              onClick={() => setShowPassword((v) => !v)}
              style={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
                color: '#6b7280',
              }}
            >
              {showPassword ? (
                <EyeTwoTone twoToneColor='#9ca3af' />
              ) : (
                <EyeInvisibleOutlined />
              )}
            </span>
          </div>
          <ul style={{ marginTop: 8, paddingLeft: 0, listStyle: 'none' }}>
            <PasswordRule
              valid={passwordRules.length(value.password)}
              text='8 a 25 caracteres'
            />
            <PasswordRule
              valid={passwordRules.upper(value.password)}
              text='Pelo menos uma letra maiúscula'
            />
            <PasswordRule
              valid={passwordRules.lower(value.password)}
              text='Pelo menos uma letra minúscula'
            />
            <PasswordRule
              valid={passwordRules.number(value.password)}
              text='Pelo menos um número'
            />
            <PasswordRule
              valid={passwordRules.special(value.password)}
              text='Pelo menos um caractere especial'
            />
          </ul>
        </Field>

        <Field>
          <label>Confirmar senha</label>

          <div style={{ position: 'relative' }}>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder='Repita a senha'
              value={value.confirm_password}
              onChange={(e) => {
                const confirm = e.target.value;
                set('confirm_password', confirm);

                if (value.password && confirm !== value.password) {
                  setPasswordError('As senhas não coincidem.');
                } else {
                  setPasswordError('');
                }
              }}
              style={{ paddingRight: 40 }}
            />

            <span
              onClick={() => setShowConfirmPassword((v) => !v)}
              style={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
                color: '#6b7280',
              }}
            >
              {showConfirmPassword ? (
                <EyeTwoTone twoToneColor='#9ca3af' />
              ) : (
                <EyeInvisibleOutlined />
              )}
            </span>
          </div>
          {passwordError && (
            <span style={{ color: '#dc2626', fontSize: 12, marginTop: 10 }}>
              {passwordError}
            </span>
          )}
        </Field>
      </FormGrid>
    </FormWrapper>
  );
}
